import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, Captions, Film, Flag, Image as ImageIcon, Link2, MapPin, Maximize, Plus, Scissors, Trash2, Upload, X,
} from 'lucide-react';
import { quizzesApi } from '../../lib/api';
import type { Quiz, QuizQuestion, VideoCaption, VideoConfig, VideoDriver, VideoSegment } from '../../types/quizzes';
import {
  CAPTION_MAX, CAPTION_TEXT_MAX, defaultVideoConfig, extractVoomlyId, formatTime, newId, roundTime, sortByAt, sortCaptions,
  unmappedQuestions, videoWarnings,
} from '../../lib/videoQuiz';
import { Button, ErrorMessage } from '../ui';
import { Switch, TagChipPicker, TimeInput, type CfTag } from './controls';
import VideoPlayerPreview from './VideoPlayerPreview';

const VIDEO_TYPES = ['video/mp4', 'video/webm'];
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function keyName(key: string): string {
  return key.split('/').pop() || key;
}

interface Props {
  quiz: Quiz;
  onChange: (patch: Partial<Quiz>) => void;
  /** Save immediately with the given quiz (used right after an upload so the media isn't orphaned). */
  onSaveNow: (quiz: Quiz) => Promise<boolean>;
  cfTags: CfTag[];
  onTagCreated: (tag: CfTag) => void;
  previewKey: number;
}

export default function VideoTab({ quiz, onChange, onSaveNow, cfTags, onTagCreated, previewKey }: Props) {
  const video: VideoConfig = quiz.video ?? defaultVideoConfig();
  const setVideo = (patch: Partial<VideoConfig>) => onChange({ video: { ...video, ...patch } });

  const videoRef = useRef<HTMLVideoElement>(null);
  const [current, setCurrent] = useState(0);
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [selectedQ, setSelectedQ] = useState<string>('');

  const isVoomly = video.driver === 'voomly';
  const [urlDraft, setUrlDraft] = useState(video.url ?? '');
  useEffect(() => { setUrlDraft(video.url ?? ''); }, [video.url]);
  const [voomlyDraft, setVoomlyDraft] = useState(video.voomlyId ?? '');
  const [voomlyError, setVoomlyError] = useState<string | null>(null);
  useEffect(() => { setVoomlyDraft(video.voomlyId ?? ''); }, [video.voomlyId]);

  // Upload state (shared by video + poster; one at a time).
  const [uploading, setUploading] = useState<null | 'video' | 'poster'>(null);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [lastUpload, setLastUpload] = useState<string | null>(null);

  // Only timed questions live on the timeline; follow-ups have no stop time.
  const questions = sortByAt(quiz.questions).filter((q) => !q.followUp);
  useEffect(() => {
    if (!selectedQ && questions.length) setSelectedQ(questions[0].id);
    if (selectedQ && !questions.some((q) => q.id === selectedQ)) setSelectedQ(questions[0]?.id ?? '');
  }, [questions, selectedQ]);

  // Voomly videos have no <video> we can scrub here; the live preview renders them instead.
  const src = isVoomly ? '' : video.mediaKey ? quizzesApi.getVideoMediaUrl(video.mediaKey) : video.url && /^https:\/\//i.test(video.url) ? video.url : '';
  const posterSrc = video.posterKey ? quizzesApi.getVideoMediaUrl(video.posterKey) : undefined;

  useEffect(() => { setMediaDuration(null); setMediaError(null); setCurrent(0); }, [src]);

  const total = Math.max(
    mediaDuration ?? 0,
    video.duration ?? 0,
    video.mainEnd || 0,
    ...video.segments.map((s) => s.end),
    ...(isVoomly ? questions.map((q) => q.at ?? 0) : []),
    1,
  );

  const seek = (t: number) => {
    const el = videoRef.current;
    const clamped = Math.max(0, Math.min(t, total));
    if (el) el.currentTime = clamped;
    setCurrent(clamped);
  };

  const now = () => roundTime(videoRef.current?.currentTime ?? current);

  // ---- uploads ----
  const upload = async (file: File, kind: 'video' | 'poster') => {
    const allowed = kind === 'video' ? VIDEO_TYPES : IMAGE_TYPES;
    if (!allowed.includes(file.type)) {
      setUploadError(kind === 'video' ? 'Please choose an MP4 or WebM video.' : 'Please choose a PNG, JPEG or WebP image.');
      return;
    }
    setUploadError(null);
    setUploading(kind);
    setProgress(0);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const res = await quizzesApi.uploadVideoMedia(quiz.id, file, { onProgress: setProgress, signal: ctrl.signal });
    abortRef.current = null;
    setUploading(null);
    if (!res.success || !res.data) {
      if (res.error !== 'aborted') setUploadError(res.message || 'Upload failed');
      return;
    }
    const key = kind === 'poster' ? (res.data.posterKey ?? res.data.mediaKey) : res.data.mediaKey;
    const nextVideo: VideoConfig = kind === 'video'
      ? { ...video, mediaKey: key, url: undefined }
      : { ...video, posterKey: key };
    const next = { ...quiz, video: nextVideo };
    onChange({ video: nextVideo });
    const saved = await onSaveNow(next);
    const size = formatBytes(res.data.size ?? file.size);
    setLastUpload(saved
      ? `Uploaded ${file.name} (${size}) and saved. Uploads count toward your plan's video storage.`
      : `Uploaded ${file.name} (${size}), but the quiz couldn't be saved — fix the error below and click Save Changes.`);
  };

  const applyUrl = () => {
    const u = urlDraft.trim();
    if (!u) { setVideo({ url: undefined }); return; }
    if (!/^https:\/\//i.test(u)) { setUploadError('The video URL must start with https://'); return; }
    setUploadError(null);
    setVideo({ url: u, mediaKey: undefined });
  };

  const applyVoomly = () => {
    const raw = voomlyDraft.trim();
    if (!raw) { setVoomlyError(null); setVideo({ voomlyId: undefined }); return; }
    const id = extractVoomlyId(raw);
    if (!id) { setVoomlyError('Couldn\'t find a Voomly video ID in that. Paste the data-id value from Voomly\'s embed code.'); return; }
    setVoomlyError(null);
    setVoomlyDraft(id);
    if (id !== video.voomlyId) setVideo({ voomlyId: id });
  };

  const setDriver = (driver: VideoDriver) => {
    if (driver === video.driver) return;
    setUploadError(null);
    setVoomlyError(null);
    // The other source's fields are kept, so switching back is lossless.
    setVideo({ driver });
  };

  // ---- captions ----
  const captions = video.captions ?? [];
  const setCaptions = (next: VideoCaption[]) => setVideo({ captions: sortCaptions(next) });
  const updateCaption = (id: string, patch: Partial<VideoCaption>) =>
    setCaptions(captions.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const addCaption = () => {
    const last = captions.reduce((m, c) => Math.max(m, c.to), 0);
    const from = roundTime(src ? now() : last);
    setCaptions([...captions, { id: newId(), from, to: roundTime(from + 3), text: '' }]);
  };

  // ---- questions ----
  const updateQuestion = (qId: string, patch: Partial<QuizQuestion>) =>
    onChange({ questions: quiz.questions.map((q) => (q.id === qId ? { ...q, ...patch } : q)) });

  const setQuestionAtNow = () => {
    if (!selectedQ) return;
    updateQuestion(selectedQ, { at: now() });
  };

  const addQuestionHere = () => {
    const at = now();
    const q: QuizQuestion = {
      id: newId(), type: 'single_choice', title: 'New Question', required: true, sortOrder: quiz.questions.length, at,
      answers: [
        { id: newId(), text: 'Option A', score: 1, next: { type: 'continue' } },
        { id: newId(), text: 'Option B', score: 0, next: { type: 'continue' } },
      ],
    };
    onChange({ questions: [...quiz.questions, q] });
    setSelectedQ(q.id);
  };

  // ---- segments ----
  const updateSegment = (id: string, patch: Partial<VideoSegment>) =>
    setVideo({ segments: video.segments.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  const addSegment = () => {
    const last = video.segments.reduce((m, s) => Math.max(m, s.end), video.mainEnd || 0);
    const start = roundTime(Math.max(last, now()));
    setVideo({ segments: [...video.segments, { id: newId(), label: `Clip ${video.segments.length + 1}`, start, end: roundTime(Math.min(start + 10, total)) }] });
  };

  const removeSegment = (id: string) => {
    const used = quiz.questions.some((q) => q.answers.some((a) => a.next?.type === 'clip' && a.next.segmentId === id));
    if (used && !confirm('Some answers play this clip. Remove it? Those answers will go back to "Continue".')) return;
    onChange({
      video: { ...video, segments: video.segments.filter((s) => s.id !== id) },
      questions: quiz.questions.map((q) => ({
        ...q,
        answers: q.answers.map((a) => (a.next?.type === 'clip' && a.next.segmentId === id ? { ...a, next: { type: 'continue' as const } } : a)),
      })),
    });
  };

  // ---- timeline pointer ----
  const barRef = useRef<HTMLDivElement>(null);
  const seekFromPointer = (clientX: number) => {
    const el = barRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    seek(roundTime(f * total));
  };

  const warnings = videoWarnings(video, quiz.questions);
  const unmapped = unmappedQuestions(quiz.questions);
  const pct = (t: number) => `${Math.max(0, Math.min(100, (t / total) * 100))}%`;

  return (
    <div className="space-y-6">
      {/* ── Source ── */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2"><Film className="w-4 h-4" /> Video</h3>
        <div className="mb-3">
          <div className="ds-label">Video source</div>
          <div role="radiogroup" aria-label="Video source" className="inline-flex rounded-[10px] border border-line bg-surface-2 p-0.5">
            {([['native', 'Upload / URL'], ['voomly', 'Voomly']] as const).map(([d, label]) => (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={video.driver === d}
                onClick={() => setDriver(d)}
                className={`px-3 py-1.5 text-sm rounded-[8px] font-medium ${video.driver === d ? 'bg-surface text-accent-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {isVoomly ? (
          <div className="border border-line rounded-lg p-3 bg-surface-2">
            <label className="ds-label" htmlFor={`voomly-id-${quiz.id}`}>Voomly video ID</label>
            <div className="flex items-center gap-2">
              <input
                id={`voomly-id-${quiz.id}`}
                type="text"
                value={voomlyDraft}
                onChange={(e) => { setVoomlyDraft(e.target.value); setVoomlyError(null); }}
                onBlur={applyVoomly}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyVoomly(); } }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  const id = extractVoomlyId(text);
                  if (id && text.trim() !== id) {
                    e.preventDefault();
                    setVoomlyDraft(id);
                    setVoomlyError(null);
                    if (id !== video.voomlyId) setVideo({ voomlyId: id });
                  }
                }}
                placeholder="e.g. aBcD1234efGh — or paste Voomly's embed code / share link"
                className={`ds-input flex-1 font-mono ${voomlyError ? '!border-danger' : ''}`}
              />
              <Button variant="ghost" icon={Link2} onClick={applyVoomly} type="button">Use</Button>
            </div>
            {voomlyError && <p className="text-xs text-danger mt-1.5">{voomlyError}</p>}
            <p className="text-xs text-ink-3 mt-2">
              In Voomly, open the video → Share / Embed and copy the embed code. The ID is the <code className="font-mono">data-id="…"</code> value in it.
              You can paste the whole embed code or the share link here and we'll pull the ID out.
            </p>
            <p className="text-xs text-ink-3 mt-1">Put branch clips at the end of the same Voomly video, after the main part.</p>
          </div>
        ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-line rounded-lg p-3 bg-surface-2">
            <label className="ds-label">Upload MP4 or WebM</label>
            <div className="flex items-center gap-2 flex-wrap">
              <label className={`ds-btn ds-btn-ghost cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload className="w-4 h-4" /> Choose video
                <input type="file" accept="video/mp4,video/webm" className="hidden" disabled={!!uploading}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f, 'video'); }} />
              </label>
              {video.mediaKey && <span className="text-xs text-ink-3 font-mono truncate max-w-[220px]" title={video.mediaKey}>{keyName(video.mediaKey)}</span>}
            </div>
            <p className="text-xs text-ink-3 mt-2">Put branch clips at the end of the same file, after the main video.</p>
          </div>
          <div className="border border-line rounded-lg p-3 bg-surface-2">
            <label className="ds-label">…or paste an https video URL</label>
            <div className="flex items-center gap-2">
              <input type="url" value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} onBlur={applyUrl}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyUrl(); } }}
                placeholder="https://cdn.example.com/video.mp4" className="ds-input flex-1" />
              <Button variant="ghost" icon={Link2} onClick={applyUrl} type="button">Use</Button>
            </div>
            <p className="text-xs text-ink-3 mt-2">A direct link to an MP4/WebM file (not a YouTube/Vimeo page). Using a URL replaces an uploaded file.</p>
          </div>
        </div>

        {uploading && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-ink-3 mb-1">
              <span>Uploading {uploading === 'poster' ? 'poster' : 'video'}… {Math.round(progress * 100)}%</span>
              <button type="button" onClick={() => abortRef.current?.abort()} className="flex items-center gap-1 hover:text-danger"><X className="w-3.5 h-3.5" /> Cancel</button>
            </div>
            <div className="h-2 rounded-full bg-surface-2 border border-line overflow-hidden">
              <div className="h-full bg-accent transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
          </div>
        )}
        {lastUpload && !uploading && !uploadError && <p className="mt-2 text-xs text-ink-2">{lastUpload}</p>}
        {uploadError && (
          <div className="mt-3 p-2.5 bg-danger-soft border border-line rounded-lg text-danger text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={uploadError} />
          </div>
        )}

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className={`ds-btn ds-btn-ghost cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
            <ImageIcon className="w-4 h-4" /> {video.posterKey ? 'Replace poster' : 'Upload poster (optional)'}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={!!uploading}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f, 'poster'); }} />
          </label>
          {video.posterKey && (
            <>
              <img src={posterSrc} alt="Poster" className="h-10 rounded border border-line" />
              <button type="button" onClick={() => setVideo({ posterKey: undefined })} className="text-xs text-ink-3 hover:text-danger">Remove poster</button>
            </>
          )}
        </div>
        </>
        )}
      </section>

      {/* ── Timeline editor ── */}
      <section>
        <h3 className="text-sm font-semibold text-ink mb-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> Timeline</h3>
        {isVoomly ? (
          <ManualTimeline
            video={video}
            questions={questions}
            total={total}
            selectedQ={selectedQ}
            onSelect={setSelectedQ}
            onQuestionAt={(id, at) => updateQuestion(id, { at })}
            onMainEnd={(v) => setVideo({ mainEnd: v })}
          />
        ) : !src ? (
          <p className="text-sm text-ink-3 border border-dashed border-line rounded-lg p-6 text-center">Add a video above to place questions on the timeline.</p>
        ) : (
          <div className="space-y-3">
            <div className="bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={src}
                poster={posterSrc}
                controls
                playsInline
                preload="metadata"
                className="w-full max-h-[420px] mx-auto block"
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget.duration;
                  if (Number.isFinite(d) && d > 0) {
                    setMediaDuration(d);
                    const dur = roundTime(d);
                    if (video.duration !== dur || !(video.mainEnd > 0)) {
                      setVideo({ duration: dur, mainEnd: video.mainEnd > 0 ? video.mainEnd : dur });
                    }
                  }
                }}
                onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
                onSeeked={(e) => setCurrent(e.currentTarget.currentTime)}
                onError={() => setMediaError('This video could not be loaded in the browser. Check the URL or upload the file instead.')}
              />
            </div>
            {mediaError && <p className="text-sm text-danger flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {mediaError}</p>}

            {/* Scrub bar */}
            <div>
              <div
                ref={barRef}
                role="slider"
                aria-label="Timeline"
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={current}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft') { e.preventDefault(); seek(roundTime(current - (e.shiftKey ? 1 : 0.1))); }
                  if (e.key === 'ArrowRight') { e.preventDefault(); seek(roundTime(current + (e.shiftKey ? 1 : 0.1))); }
                }}
                onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); seekFromPointer(e.clientX); }}
                onPointerMove={(e) => { if (e.buttons & 1) seekFromPointer(e.clientX); }}
                className="relative h-12 rounded-lg bg-surface-2 border border-line cursor-pointer select-none touch-none"
              >
                {/* main video area */}
                <div className="absolute inset-y-0 left-0 bg-accent-soft rounded-l-lg" style={{ width: pct(video.mainEnd || total) }} />
                {/* segments */}
                {video.segments.map((s) => (
                  <div key={s.id} title={`${s.label}: ${formatTime(s.start)} – ${formatTime(s.end)}`}
                    className="absolute top-1 bottom-1 rounded bg-violet-soft border border-[var(--violet)] overflow-hidden"
                    style={{ left: pct(s.start), width: `calc(${pct(s.end - s.start)})` }}>
                    <span className="text-[10px] px-1 text-[var(--violet)] font-medium whitespace-nowrap">{s.label}</span>
                  </div>
                ))}
                {/* main end */}
                {video.mainEnd > 0 && (
                  <div className="absolute inset-y-0 w-0.5 bg-ink" style={{ left: pct(video.mainEnd) }} title={`Main video ends ${formatTime(video.mainEnd)}`}>
                    <Flag className="w-3 h-3 absolute -top-3.5 -left-1 text-ink" />
                  </div>
                )}
                {/* question markers */}
                {questions.map((q, i) => q.at !== undefined && (
                  <button
                    key={q.id}
                    type="button"
                    title={`${i + 1}. ${q.title} (${formatTime(q.at)})`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); setSelectedQ(q.id); seek(q.at ?? 0); }}
                    className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center border-2 ${selectedQ === q.id ? 'bg-accent text-white border-white' : 'bg-surface text-accent-ink border-accent'}`}
                    style={{ left: pct(q.at) }}
                  >
                    {i + 1}
                  </button>
                ))}
                {/* playhead */}
                <div className="absolute inset-y-0 w-0.5 bg-danger pointer-events-none" style={{ left: pct(current) }} />
              </div>
              <div className="flex justify-between text-xs text-ink-3 mt-1 font-mono">
                <span>{formatTime(current)}</span>
                <span>{formatTime(total)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="ds-label">Question</label>
                <select value={selectedQ} onChange={(e) => setSelectedQ(e.target.value)} className="ds-input w-56">
                  {questions.length === 0 && <option value="">No questions yet</option>}
                  {questions.map((q, i) => <option key={q.id} value={q.id}>{i + 1}. {q.title} {q.at !== undefined ? `(${formatTime(q.at)})` : ''}</option>)}
                </select>
              </div>
              <Button type="button" icon={MapPin} onClick={setQuestionAtNow} disabled={!selectedQ}>Set question stop here</Button>
              <Button type="button" variant="ghost" icon={Plus} onClick={addQuestionHere}>Add question here</Button>
              <Button type="button" variant="ghost" icon={Flag} onClick={() => setVideo({ mainEnd: now() })}>Set main video end here</Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-ink-2">
              <span>Main video ends at</span>
              <TimeInput value={video.mainEnd} onChange={(v) => setVideo({ mainEnd: v })} ariaLabel="Main video end" />
            </div>
          </div>
        )}
      </section>

      {/* ── Segments ── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><Scissors className="w-4 h-4" /> Branch clips</h3>
          <button type="button" onClick={addSegment} className="flex items-center gap-1 px-3 py-1.5 bg-accent-soft text-accent-ink rounded-[10px] text-sm font-medium hover:opacity-90">
            <Plus className="w-4 h-4" /> Add clip
          </button>
        </div>
        <p className="text-xs text-ink-3 mb-3">A clip plays when a viewer picks an answer set to "Play clip" (Questions tab), then the main video resumes right after that question.</p>
        {video.segments.length === 0 ? (
          <p className="text-sm text-ink-3">No clips. Every answer continues the main video.</p>
        ) : (
          <div className="space-y-2">
            {video.segments.map((s) => (
              <div key={s.id} className="border border-line rounded-lg p-3 bg-surface-2 flex flex-wrap items-center gap-2">
                <input type="text" value={s.label} onChange={(e) => updateSegment(s.id, { label: e.target.value })} className="ds-input w-full sm:w-44" placeholder="Clip name" />
                <div className="flex items-center gap-1">
                  <span className="text-xs text-ink-3">Start</span>
                  <TimeInput value={s.start} onChange={(v) => updateSegment(s.id, { start: v })} ariaLabel="Clip start" />
                  {!isVoomly && <button type="button" onClick={() => updateSegment(s.id, { start: now() })} disabled={!src} className="text-xs text-accent-ink hover:opacity-80 disabled:opacity-40">from current</button>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-ink-3">End</span>
                  <TimeInput value={s.end} onChange={(v) => updateSegment(s.id, { end: v })} ariaLabel="Clip end" />
                  {!isVoomly && <button type="button" onClick={() => updateSegment(s.id, { end: now() })} disabled={!src} className="text-xs text-accent-ink hover:opacity-80 disabled:opacity-40">from current</button>}
                </div>
                {!isVoomly && <button type="button" onClick={() => { seek(s.start); videoRef.current?.play().catch(() => undefined); }} disabled={!src} className="text-xs text-ink-3 hover:text-ink disabled:opacity-40">Play</button>}
                <button type="button" onClick={() => removeSegment(s.id)} className="ml-auto text-ink-3 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Captions ── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-ink flex items-center gap-2"><Captions className="w-4 h-4" /> Captions</h3>
          <button type="button" onClick={addCaption} disabled={captions.length >= CAPTION_MAX}
            className="flex items-center gap-1 px-3 py-1.5 bg-accent-soft text-accent-ink rounded-[10px] text-sm font-medium hover:opacity-90 disabled:opacity-40">
            <Plus className="w-4 h-4" /> Add caption
          </button>
        </div>
        <p className="text-xs text-ink-3 mb-3">
          Text shown over the main video between two times. Personalize with <code className="font-mono">{'{{first_name|there}}'}</code>
          {' '}(also <code className="font-mono">{'{{last_name|…}}'}</code>, <code className="font-mono">{'{{email|…}}'}</code>) — the part after <code className="font-mono">|</code> is used when the name isn't known.
        </p>
        {captions.length === 0 ? (
          <p className="text-sm text-ink-3">No captions.</p>
        ) : (
          <div className="space-y-2">
            {captions.map((c) => {
              const bad = !(c.to > c.from);
              return (
                <div key={c.id} className="border border-line rounded-lg p-3 bg-surface-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-ink-3">From</span>
                      <TimeInput value={c.from} onChange={(v) => updateCaption(c.id, { from: v })} ariaLabel="Caption start" className={bad ? '!border-danger' : ''} />
                      {src && <button type="button" onClick={() => updateCaption(c.id, { from: now() })} className="text-xs text-accent-ink hover:opacity-80">set from current time</button>}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-ink-3">To</span>
                      <TimeInput value={c.to} onChange={(v) => updateCaption(c.id, { to: v })} ariaLabel="Caption end" className={bad ? '!border-danger' : ''} />
                      {src && <button type="button" onClick={() => updateCaption(c.id, { to: now() })} className="text-xs text-accent-ink hover:opacity-80">set from current time</button>}
                    </div>
                    {src && <button type="button" onClick={() => seek(c.from)} className="text-xs text-ink-3 hover:text-ink">Go to</button>}
                    <button type="button" onClick={() => setCaptions(captions.filter((x) => x.id !== c.id))} className="ml-auto text-ink-3 hover:text-danger" aria-label="Remove caption"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div>
                    <input type="text" value={c.text} maxLength={CAPTION_TEXT_MAX}
                      onChange={(e) => setVideo({ captions: captions.map((x) => (x.id === c.id ? { ...x, text: e.target.value } : x)) })}
                      placeholder="Hey {{first_name|there}}, watch this part closely…" className="ds-input w-full" />
                    {bad && <p className="text-xs text-danger mt-1">"To" must be after "From".</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {captions.length >= CAPTION_MAX && <p className="text-xs text-ink-3 mt-2">That's the maximum of {CAPTION_MAX} captions.</p>}
      </section>

      {/* ── Behaviour ── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-line rounded-lg p-3 space-y-4">
          <div className="flex items-start gap-3">
            <Switch checked={video.enforcement.preventSkip} onChange={(v) => setVideo({ enforcement: { ...video.enforcement, preventSkip: v } })} label="Prevent skipping" />
            <div>
              <div className="text-sm font-medium text-ink-2">Prevent skipping ahead</div>
              <div className="text-xs text-ink-3">Viewers can't seek past what they've watched. Rewinding is always allowed.</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Switch checked={video.allowFullscreen !== false} onChange={(v) => setVideo({ allowFullscreen: v })} label="Let viewers go fullscreen" />
            <div>
              <div className="text-sm font-medium text-ink-2 flex items-center gap-1.5"><Maximize className="w-3.5 h-3.5" /> Let viewers go fullscreen</div>
              <div className="text-xs text-ink-3">Shows a fullscreen button; questions stay on screen in fullscreen. Hidden on iPhone, where the browser doesn't allow it.</div>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-ink-2 mb-1">Lead form timing</div>
            <div className="flex gap-4">
              {(['start', 'end'] as const).map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm">
                  <input type="radio" name={`lead-timing-${quiz.id}`} checked={video.leadTiming === t} onChange={() => setVideo({ leadTiming: t })} />
                  {t === 'start' ? 'Before the video starts' : 'After the last question'}
                </label>
              ))}
            </div>
            <p className="text-xs text-ink-3 mt-1">
              {quiz.leadCapture.enabled ? 'Fields and headline come from the Lead Capture tab.' : 'Lead capture is off — turn it on in the Lead Capture tab to collect emails.'}
            </p>
          </div>
        </div>
        <div className="border border-line rounded-lg p-3">
          <div className="text-sm font-medium text-ink-2">Tag viewers who don't finish</div>
          <p className="text-xs text-ink-3 mb-2">After 7 days without finishing, viewers who gave an email are synced with the answers they gave plus these tags.</p>
          <TagChipPicker tags={cfTags} selected={video.incompleteTagIds ?? []} onChange={(next) => setVideo({ incompleteTagIds: next })}
            connectionId={quiz.connectionId} onTagCreated={onTagCreated} />
        </div>
      </section>

      {(warnings.length > 0 || unmapped.length > 0) && (
        <section className="p-3 bg-warning-soft border border-line rounded-lg text-sm">
          <div className="flex items-center gap-2 font-medium text-warning mb-1"><AlertTriangle className="w-4 h-4" /> Before you publish</div>
          <ul className="list-disc ml-5 space-y-0.5 text-ink-2">
            {warnings.map((w) => <li key={w}>{w}</li>)}
            {unmapped.length > 0 && <li>{unmapped.length} question{unmapped.length === 1 ? '' : 's'} not mapped to a ClickFunnels field or tags — see the Questions tab.</li>}
          </ul>
        </section>
      )}

      {quiz.video && <VideoPlayerPreview quizId={quiz.id} refreshKey={previewKey} />}
    </div>
  );
}

/**
 * Timeline for drivers we can't scrub in the builder (Voomly): a read-only bar plus manual time fields.
 * Viewers' times come from the live preview below, which renders the real Voomly player.
 */
function ManualTimeline({
  video, questions, total, selectedQ, onSelect, onQuestionAt, onMainEnd,
}: {
  video: VideoConfig;
  questions: QuizQuestion[];
  total: number;
  selectedQ: string;
  onSelect: (id: string) => void;
  onQuestionAt: (id: string, at: number) => void;
  onMainEnd: (v: number) => void;
}) {
  const pct = (t: number) => `${Math.max(0, Math.min(100, (t / total) * 100))}%`;
  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-3">
        Voomly videos can't be scrubbed here, so type the times in. Play the live preview below to find the moments you want — it uses the real Voomly player.
      </p>
      <div className="relative h-12 rounded-lg bg-surface-2 border border-line select-none" aria-hidden="true">
        <div className="absolute inset-y-0 left-0 bg-accent-soft rounded-l-lg" style={{ width: pct(video.mainEnd || total) }} />
        {video.segments.map((s) => (
          <div key={s.id} title={`${s.label}: ${formatTime(s.start)} – ${formatTime(s.end)}`}
            className="absolute top-1 bottom-1 rounded bg-violet-soft border border-[var(--violet)] overflow-hidden"
            style={{ left: pct(s.start), width: pct(s.end - s.start) }}>
            <span className="text-[10px] px-1 text-[var(--violet)] font-medium whitespace-nowrap">{s.label}</span>
          </div>
        ))}
        {video.mainEnd > 0 && (
          <div className="absolute inset-y-0 w-0.5 bg-ink" style={{ left: pct(video.mainEnd) }} title={`Main video ends ${formatTime(video.mainEnd)}`}>
            <Flag className="w-3 h-3 absolute -top-3.5 -left-1 text-ink" />
          </div>
        )}
        {questions.map((q, i) => q.at !== undefined && (
          <button
            key={q.id}
            type="button"
            tabIndex={-1}
            title={`${i + 1}. ${q.title} (${formatTime(q.at)})`}
            onClick={() => onSelect(q.id)}
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center border-2 ${selectedQ === q.id ? 'bg-accent text-white border-white' : 'bg-surface text-accent-ink border-accent'}`}
            style={{ left: pct(q.at) }}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-ink-3 -mt-2 font-mono">
        <span>0:00.0</span>
        <span>{formatTime(total)}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-ink-2">
        <Flag className="w-4 h-4" />
        <span>Main video ends at</span>
        <TimeInput value={video.mainEnd || undefined} onChange={onMainEnd} ariaLabel="Main video end" />
        <span className="text-xs text-ink-3">Branch clips go after this point.</span>
      </div>
      {questions.length > 0 && (
        <div className="border border-line rounded-lg divide-y divide-[var(--line)]">
          {questions.map((q, i) => (
            <div key={q.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${selectedQ === q.id ? 'bg-accent-soft' : ''}`} onFocus={() => onSelect(q.id)}>
              <span className="w-5 text-xs font-bold text-ink-3">{i + 1}</span>
              <span className="flex-1 min-w-0 truncate text-ink-2">{q.title || 'Untitled question'}</span>
              <span className="text-xs text-ink-3">Stops at</span>
              <TimeInput value={q.at} onChange={(v) => onQuestionAt(q.id, v)} ariaLabel={`Stop time for ${q.title}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
